import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { addSlashes } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  Assets,
  AssetRequestForm,
  MsAssignedAssetsPage,
  MsRequestedAssetsPage,
} from "@/utils/lazyLoadComponents";
import { MSAssetRequestView } from "@/pages/(home)/(my-space)/assets/_components/MSAssetRequestView";

// ─── Path Segments ────────────────────────────────────────────────────────────
export const MS_ASSETS_DYNAMIC_ID_KEY = "id";

export enum MS_ASSETS_PATH_SEGMENTS {
  ROOT = "assets",
  ASSIGNED_ASSETS = "assigned-assets",
  REQUESTED_ASSETS = "requested-assets",
  REQUEST = "request",
  DETAILS = `:${MS_ASSETS_DYNAMIC_ID_KEY}`,
  EDIT = "edit",
}

// ─── Route Definition ─────────────────────────────────────────────────────────
export const mySpaceAssetsRoutes: RouteObject = {
  path: MS_ASSETS_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Assets" },
  HydrateFallback: Spinner,
  // No Component here — only the 2 tab index pages get the page-level tabs layout
  children: [
    // ── Page-level tabs layout: each tab owns its own stats + table ──────────
    {
      Component: Assets,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(MS_ASSETS_PATH_SEGMENTS.ASSIGNED_ASSETS),
        },
        {
          path: MS_ASSETS_PATH_SEGMENTS.ASSIGNED_ASSETS,
          handle: { breadcrumb: "Assigned Assets" },
          Component: MsAssignedAssetsPage,
        },
        {
          path: MS_ASSETS_PATH_SEGMENTS.REQUESTED_ASSETS,
          handle: { breadcrumb: "Requested Assets" },
          Component: MsRequestedAssetsPage,
        },
      ],
    },

    // ── Requested assets detail routes (no stats, no tabs) ──────────────────
    {
      path: MS_ASSETS_PATH_SEGMENTS.REQUESTED_ASSETS,
      handle: { breadcrumb: "Requested Assets" },
      children: [
        {
          path: MS_ASSETS_PATH_SEGMENTS.DETAILS,
          handle: { breadcrumb: "Asset Request Details" },
          Component: MSAssetRequestView,
        },
        {
          path: `${MS_ASSETS_PATH_SEGMENTS.DETAILS}/${MS_ASSETS_PATH_SEGMENTS.EDIT}`,
          handle: { breadcrumb: "Edit" },
          Component: AssetRequestForm,
        },
      ],
    },

    // ── Request new asset (no stats, no tabs) ────────────────────────────────
    {
      path: MS_ASSETS_PATH_SEGMENTS.REQUEST,
      handle: { breadcrumb: "Request New Asset" },
      Component: AssetRequestForm,
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const MS_ASSETS_PATHS = Object.freeze({
  ROOT: addSlashes(MS_ASSETS_PATH_SEGMENTS.ROOT),
  ASSIGNED_ASSETS: addSlashes(
    MS_ASSETS_PATH_SEGMENTS.ROOT,
    MS_ASSETS_PATH_SEGMENTS.ASSIGNED_ASSETS,
  ),
  REQUESTED_ASSETS: addSlashes(
    MS_ASSETS_PATH_SEGMENTS.ROOT,
    MS_ASSETS_PATH_SEGMENTS.REQUESTED_ASSETS,
  ),
  REQUESTED_ASSET_VIEW: (id: string | number) =>
    addSlashes(
      MS_ASSETS_PATH_SEGMENTS.ROOT,
      MS_ASSETS_PATH_SEGMENTS.REQUESTED_ASSETS,
      String(id),
    ),
  REQUESTED_ASSET_EDIT: (id: string | number) =>
    addSlashes(
      MS_ASSETS_PATH_SEGMENTS.ROOT,
      MS_ASSETS_PATH_SEGMENTS.REQUESTED_ASSETS,
      String(id),
      MS_ASSETS_PATH_SEGMENTS.EDIT,
    ),
  REQUEST: addSlashes(
    MS_ASSETS_PATH_SEGMENTS.ROOT,
    MS_ASSETS_PATH_SEGMENTS.REQUEST,
  ),
});
